/**
 * MSPDI (Microsoft Project XML) -> domain Project 匯入。
 *
 * 設計原則：
 * - 絕不 throw（除非連 XML 都無法被 parser 接受，此時仍回傳一個最小合法
 *   Project 並附上 Error 等級 Diagnostic，呼叫端可自行決定要不要視為失敗）。
 * - 未知／缺漏欄位一律有合理預設值。
 * - 無法對應或忽略的內容一律記錄為 Diagnostic，不靜默吞掉。
 *
 * 回傳型別刻意設計為 `{ project, diagnostics }` 而非單純 `Project`：
 * 需求明確要求「無法對應的內容要收集成 Diagnostic[] 一併回傳」，
 * 若只回傳 Project 則無處安放 diagnostics，因此以物件包裹兩者。
 */

import {
  assignmentId,
  calendarId as makeCalendarId,
  dependencyId,
  minutes,
  money,
  percent,
  projectId,
  ratePerMinute,
  resourceId,
  taskId,
  units,
  type AssignmentId,
  type CalendarId,
  type ISODate,
  type ISODateTime,
  type Minutes,
  type ResourceId,
  type TaskId,
} from '../../domain/branded.js';
import type { Project, ScheduleFrom } from '../../domain/project.js';
import type { Task, AccrualType, ConstraintType, TaskType } from '../../domain/task.js';
import type { Dependency, DependencyType } from '../../domain/dependency.js';
import type {
  Calendar,
  CalendarException,
  DurationConversion,
  NonEmptyWorkingTimes,
  WeekDays,
  WeekDaySpec,
  WorkingTime,
} from '../../domain/calendar.js';
import type { RateTableName, Resource, ResourceAvailability, ResourceRateTables, ResourceType } from '../../domain/resource.js';
import type { Assignment } from '../../domain/assignment.js';
import type { BudgetAssignment } from '../../domain/budget.js';
import type { Baseline, BaselineAssignmentSnapshot, BaselineNumber, BaselineTaskSnapshot } from '../../domain/baseline.js';
import type {
  TimephasedCostSlice,
  TimephasedSet,
  TimephasedWorkSlice,
} from '../../domain/timephased.js';
import type { Diagnostic } from '../../domain/errors.js';

import { DEFAULT_DURATION_CONVERSION, parseIsoDuration } from './duration.js';
import { parseMspdiDate, parseMspdiDateTime } from './datetime.js';
import {
  ACCRUAL_TYPE_BY_MSPDI,
  CONSTRAINT_TYPE_BY_MSPDI,
  DEPENDENCY_TYPE_BY_MSPDI,
  MSPDI_ELAPSED_LAG_FORMATS,
  MSPDI_PERCENT_LAG_FORMATS,
  RESOURCE_TYPE_BY_MSPDI,
  TASK_TYPE_BY_MSPDI,
  WEEKDAY_INDEX_BY_MSPDI_DAY_TYPE,
} from './constants.js';
import {
  asArray,
  asNode,
  attrNumber,
  childBoolean,
  childNumber,
  childText,
  createMspdiParser,
  parseCurrencyNumber,
  type XmlNode,
} from './xml.js';

export interface MspdiImportResult {
  readonly project: Project;
  readonly diagnostics: readonly Diagnostic[];
}

interface ImportContext {
  readonly diagnostics: Diagnostic[];
  readonly conversion: DurationConversion;
  /** UID -> id，「保留第一筆」：重複 UID 只有第一次出現的實體會被記錄在這裡，
   * 供其他實體以 UID 交叉參照時使用（例如 PredecessorUID、CalendarUID）。 */
  readonly calendarUidToId: Map<number, CalendarId>;
  readonly taskUidToId: Map<number, TaskId>;
  readonly resourceUidToId: Map<number, ResourceId>;
  readonly assignmentUidToId: Map<number, AssignmentId>;
  /** 已配發過的 id 全集（含重複 UID 重新配發的），用來確保新配發的 id 不會撞名。 */
  readonly usedCalendarIds: Set<string>;
  readonly usedTaskIds: Set<string>;
  readonly usedResourceIds: Set<string>;
  readonly usedAssignmentIds: Set<string>;
  /**
   * 每個 Task XML 節點自己的 id（不同於 `taskUidToId`：後者重複 UID 時只保留第一筆，
   * 這裡則是「這個節點自己」拿到的 id，重複 UID 的第二筆也會有自己不衝突的 id）。
   * 供 importTasks / importDependencies / importBaselines 在處理「這個節點自己是誰」
   * 時使用；解析「這個節點參照到哪個 UID」時仍要用 `taskUidToId`（保留第一筆語意）。
   */
  readonly taskIdByNode: Map<XmlNode, TaskId>;
  /**
   * 每個 Assignment XML 節點自己的 id（同 `taskIdByNode` 的道理：`assignmentUidToId`
   * 重複 UID 時只保留第一筆，這裡則是「這個節點自己」拿到的 id）。importBaselines
   * 在把 `<Assignment><Baseline>` 掛回對應的 Assignment 時必須用這個，不能用
   * `assignmentUidToId`——否則重複 UID 的第二筆指派，它自己的 baseline 會被誤掛到
   * 第一筆同 UID 的指派上（與 taskIdByNode 要解決的是同一類「自我參照」問題）。
   */
  readonly assignmentIdByNode: Map<XmlNode, AssignmentId>;
}

/**
 * 為某個 UID 配發實體 id，並正確處理「重複 UID」：
 * - 第一次看到某個 UID：配發正常 id、記錄到 `primaryMap`（供其他實體交叉參照）。
 * - 之後再看到同一個 UID：`primaryMap` 保持指向第一筆（交叉參照的語意不變），
 *   但這一筆「自己」拿到一個不衝突的新 id（`${uid}-dup${n}`），並記一筆 Warning
 *   Diagnostic —— 不可讓 map 靜默覆寫，也不可讓兩個實體共用同一個 id。
 */
function resolveUniqueId<TId extends string>(
  ctx: ImportContext,
  usedIds: Set<string>,
  primaryMap: Map<number, TId>,
  uid: number,
  makeId: (raw: string) => TId,
  entityLabel: string,
): TId {
  const isDuplicate = primaryMap.has(uid);
  if (!isDuplicate) {
    const candidate = makeId(String(uid));
    primaryMap.set(uid, candidate);
    usedIds.add(candidate);
    return candidate;
  }
  let n = 1;
  let candidate = makeId(`${uid}-dup${n}`);
  while (usedIds.has(candidate)) {
    n += 1;
    candidate = makeId(`${uid}-dup${n}`);
  }
  usedIds.add(candidate);
  warn(
    ctx,
    'MSPDI_DUPLICATE_UID',
    `${entityLabel} 出現重複 UID=${uid}；其他實體對此 UID 的參照一律指向第一筆，` +
      `這一筆重新配發不衝突的內部 id="${candidate}"。`,
  );
  return candidate;
}

function warn(ctx: ImportContext, code: string, message: string): void {
  ctx.diagnostics.push({ severity: 'Warning', code, message });
}

function info(ctx: ImportContext, code: string, message: string): void {
  ctx.diagnostics.push({ severity: 'Info', code, message });
}

// ---------------------------------------------------------------------------
// 日期／日期時間欄位解析（每個欄位獨立 try/catch，單一壞日期絕不中止整份匯入）
// ---------------------------------------------------------------------------

/**
 * 解析「必要」的日期時間欄位：值缺漏時靜默套用 fallback（正常情況，不記診斷）；
 * 值存在但無法解析（形狀錯誤或語意非法，例如 2 月 31 日）時記一筆 Warning
 * 並套用 fallback，該筆任務／指派／基準線仍會被保留、繼續匯入其餘內容。
 */
function parseRequiredDateTime(
  ctx: ImportContext,
  value: string | undefined,
  fieldLabel: string,
  fallback: ISODateTime,
): ISODateTime {
  if (value === undefined) {
    return fallback;
  }
  try {
    const parsed = parseMspdiDateTime(value);
    if (parsed === null) {
      warn(ctx, 'MSPDI_DATETIME_UNPARSEABLE', `${fieldLabel} 無法解析的日期時間 "${value}"，已使用預設值繼續匯入。`);
      return fallback;
    }
    return parsed;
  } catch (error) {
    warn(
      ctx,
      'MSPDI_DATETIME_UNPARSEABLE',
      `${fieldLabel} 解析時發生例外（${error instanceof Error ? error.message : String(error)}），已使用預設值繼續匯入。`,
    );
    return fallback;
  }
}

/** 解析「選填」的日期時間欄位：缺漏或無法解析都回傳 null；無法解析時額外記一筆 Warning。 */
function parseOptionalDateTime(
  ctx: ImportContext,
  value: string | undefined,
  fieldLabel: string,
): ISODateTime | null {
  if (value === undefined) {
    return null;
  }
  try {
    const parsed = parseMspdiDateTime(value);
    if (parsed === null) {
      warn(ctx, 'MSPDI_DATETIME_UNPARSEABLE', `${fieldLabel} 無法解析的日期時間 "${value}"，已忽略此欄位。`);
    }
    return parsed;
  } catch (error) {
    warn(
      ctx,
      'MSPDI_DATETIME_UNPARSEABLE',
      `${fieldLabel} 解析時發生例外（${error instanceof Error ? error.message : String(error)}），已忽略此欄位。`,
    );
    return null;
  }
}

/** 解析「選填」的純日期欄位：缺漏或無法解析都回傳 null；無法解析時額外記一筆 Warning。 */
function parseOptionalDate(
  ctx: ImportContext,
  value: string | undefined,
  fieldLabel: string,
): ISODate | null {
  if (value === undefined) {
    return null;
  }
  try {
    const parsed = parseMspdiDate(value);
    if (parsed === null) {
      warn(ctx, 'MSPDI_DATE_UNPARSEABLE', `${fieldLabel} 無法解析的日期 "${value}"，已忽略此欄位。`);
    }
    return parsed;
  } catch (error) {
    warn(
      ctx,
      'MSPDI_DATE_UNPARSEABLE',
      `${fieldLabel} 解析時發生例外（${error instanceof Error ? error.message : String(error)}），已忽略此欄位。`,
    );
    return null;
  }
}

// ---------------------------------------------------------------------------
// 進入點
// ---------------------------------------------------------------------------

export function importMspdi(xml: string): MspdiImportResult {
  const diagnostics: Diagnostic[] = [];

  let root: XmlNode | undefined;
  try {
    const parser = createMspdiParser();
    const parsed = parser.parse(xml) as unknown;
    root = asNode(asNode(parsed)?.['Project']);
  } catch (error) {
    diagnostics.push({
      severity: 'Error',
      code: 'MSPDI_PARSE_FAILED',
      message: `XML 解析失敗：${error instanceof Error ? error.message : String(error)}`,
    });
  }

  if (!root) {
    diagnostics.push({
      severity: 'Error',
      code: 'MSPDI_NO_PROJECT_ROOT',
      message: '找不到 <Project> 根節點，回傳空白專案。',
    });
    return { project: emptyProject(), diagnostics };
  }

  const conversion = parseDurationConversion(root);
  const ctx: ImportContext = {
    diagnostics,
    conversion,
    calendarUidToId: new Map(),
    taskUidToId: new Map(),
    resourceUidToId: new Map(),
    assignmentUidToId: new Map(),
    usedCalendarIds: new Set(),
    usedTaskIds: new Set(),
    usedResourceIds: new Set(),
    usedAssignmentIds: new Set(),
    taskIdByNode: new Map(),
    assignmentIdByNode: new Map(),
  };

  resolveAllTaskIds(root, ctx);

  const calendars = importCalendars(root, ctx);
  const defaultCalendarUid = childNumber(root, 'CalendarUID');
  let defaultCalendarId =
    defaultCalendarUid !== undefined ? ctx.calendarUidToId.get(defaultCalendarUid) : undefined;
  if (defaultCalendarId === undefined) {
    defaultCalendarId = calendars[0]?.id;
  }
  if (defaultCalendarId === undefined) {
    const fallback = defaultStandardCalendar();
    calendars.push(fallback);
    defaultCalendarId = fallback.id;
    warn(ctx, 'MSPDI_NO_CALENDAR', '找不到任何日曆定義，已建立預設標準日曆。');
  }

  const tasks = importTasks(root, ctx);
  const dependencies = importDependencies(root, ctx);
  const resources = importResources(root, ctx);
  const assignments = importAssignments(root, ctx, tasks);
  const budgetAssignments = importBudgetAssignments(root, ctx, resources);
  const baselines = importBaselines(root, ctx, budgetAssignments);

  const scheduleFromStart = childBoolean(root, 'ScheduleFromStart');
  const scheduleFrom: ScheduleFrom = scheduleFromStart === false ? 'ProjectFinish' : 'ProjectStart';

  const startDate = parseRequiredDateTime(ctx, childText(root, 'StartDate'), 'Project StartDate', defaultStart());
  const finishDate = parseRequiredDateTime(ctx, childText(root, 'FinishDate'), 'Project FinishDate', defaultFinish());
  const statusDate = parseOptionalDateTime(ctx, childText(root, 'StatusDate'), 'Project StatusDate');

  const name = childText(root, 'Name') ?? childText(root, 'Title') ?? 'Imported Project';
  const currency = childText(root, 'CurrencySymbol') ?? 'USD';

  const project: Project = {
    id: projectId('mspdi-import'),
    name,
    startDate,
    finishDate,
    scheduleFrom,
    statusDate,
    defaultCalendarId,
    currency,
    tasks,
    dependencies,
    resources,
    assignments,
    ...(budgetAssignments.length > 0 ? { budgetAssignments } : {}),
    calendars,
    baselines,
    customFields: [],
    options: {
      defaultTaskType: 'FixedUnits',
      newTasksAreManual: false,
      durationConversion: conversion,
      showEstimatedDurations: false,
      moveActualsToStatusDate: false,
    },
  };

  return { project, diagnostics };
}

// ---------------------------------------------------------------------------
// Project 層
// ---------------------------------------------------------------------------

function parseDurationConversion(root: XmlNode): DurationConversion {
  const minutesPerDay = childNumber(root, 'MinutesPerDay');
  const minutesPerWeek = childNumber(root, 'MinutesPerWeek');
  const daysPerMonth = childNumber(root, 'DaysPerMonth');
  return {
    minutesPerDay: minutesPerDay !== undefined ? minutes(minutesPerDay) : DEFAULT_DURATION_CONVERSION.minutesPerDay,
    minutesPerWeek: minutesPerWeek !== undefined ? minutes(minutesPerWeek) : DEFAULT_DURATION_CONVERSION.minutesPerWeek,
    daysPerMonth: daysPerMonth ?? DEFAULT_DURATION_CONVERSION.daysPerMonth,
  };
}

function defaultStart(): ISODateTime {
  return parseMspdiDateTime('2000-01-01T08:00:00') as ISODateTime;
}
function defaultFinish(): ISODateTime {
  return parseMspdiDateTime('2000-01-01T17:00:00') as ISODateTime;
}

function defaultStandardCalendar(): Calendar {
  const workingTimes: NonEmptyWorkingTimes = [
    { start: minutes(8 * 60), end: minutes(12 * 60) },
    { start: minutes(13 * 60), end: minutes(17 * 60) },
  ];
  const workingDay: WeekDaySpec = { working: true, times: workingTimes };
  const restDay: WeekDaySpec = { working: false };
  const weekDays: WeekDays = [restDay, workingDay, workingDay, workingDay, workingDay, workingDay, restDay];
  return {
    id: makeCalendarId('cal-mspdi-default'),
    name: 'Standard',
    baseCalendarId: null,
    weekDays,
    exceptions: [],
  };
}

function emptyProject(): Project {
  const calendar = defaultStandardCalendar();
  return {
    id: projectId('mspdi-import'),
    name: 'Imported Project',
    startDate: defaultStart(),
    finishDate: defaultFinish(),
    scheduleFrom: 'ProjectStart',
    statusDate: null,
    defaultCalendarId: calendar.id,
    currency: 'USD',
    tasks: [],
    dependencies: [],
    resources: [],
    assignments: [],
    calendars: [calendar],
    baselines: [],
    customFields: [],
  };
}

// ---------------------------------------------------------------------------
// Calendars
// ---------------------------------------------------------------------------

function importCalendars(root: XmlNode, ctx: ImportContext): Calendar[] {
  const calendarsNode = asNode(root['Calendars']);
  const rawCalendars = asArray(calendarsNode?.['Calendar']).map(asNode).filter((n): n is XmlNode => n !== undefined);

  // 第一遍：先依序配發 UID -> CalendarId（重複 UID 保留第一筆的交叉參照，
  // 重複的那一筆拿到另一個不衝突的 id，見 resolveUniqueId），供 BaseCalendarUID 交叉參照。
  const idByNode = new Map<XmlNode, CalendarId>();
  for (const node of rawCalendars) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      continue;
    }
    const id = resolveUniqueId(ctx, ctx.usedCalendarIds, ctx.calendarUidToId, uid, (raw) => makeCalendarId(`cal${raw}`), 'Calendar');
    idByNode.set(node, id);
  }

  const result: Calendar[] = [];
  for (const node of rawCalendars) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      warn(ctx, 'MSPDI_CALENDAR_NO_UID', `略過一筆缺少 UID 的 Calendar（Name="${childText(node, 'Name') ?? '(未命名)'}"）。`);
      continue;
    }
    const id = idByNode.get(node) as CalendarId;
    const name = childText(node, 'Name') ?? `Calendar ${uid}`;
    const baseUid = childNumber(node, 'BaseCalendarUID');
    const baseCalendarId =
      baseUid !== undefined && baseUid !== uid ? ctx.calendarUidToId.get(baseUid) ?? null : null;

    const { weekDays, exceptions } = importWeekDays(node, ctx, id);

    result.push({ id, name, baseCalendarId, weekDays, exceptions });
  }
  return result;
}

function importWeekDays(
  calendarNode: XmlNode,
  ctx: ImportContext,
  calendarIdForDiagnostics: CalendarId,
): { weekDays: WeekDays; exceptions: CalendarException[] } {
  const weekDaysNode = asNode(calendarNode['WeekDays']);
  const entries = asArray(weekDaysNode?.['WeekDay']).map(asNode).filter((n): n is XmlNode => n !== undefined);

  // 未出現在 XML 的星期幾維持 `null`（未指定，往 base calendar 繼承）。
  const mutableWeek: WeekDaySpec[] = [null, null, null, null, null, null, null];
  const exceptions: CalendarException[] = [];

  for (const entry of entries) {
    const dayType = childNumber(entry, 'DayType');
    if (dayType === undefined) {
      continue;
    }
    const dayWorking = childBoolean(entry, 'DayWorking') ?? false;
    const workingTimes = importWorkingTimes(entry);

    if (dayType === 0) {
      // `TimePeriod` 在 xml.ts 中被列為「一律以陣列解析」，這裡取第一筆。
      const timePeriod = asNode(asArray(entry['TimePeriod'])[0]);
      const fromText = childText(timePeriod, 'FromDate');
      const from = parseOptionalDate(ctx, fromText, `日曆 ${calendarIdForDiagnostics} 的例外 FromDate`);
      const to = parseOptionalDate(ctx, childText(timePeriod, 'ToDate'), `日曆 ${calendarIdForDiagnostics} 的例外 ToDate`) ?? from;
      if (!from) {
        if (fromText === undefined) {
          warn(
            ctx,
            'MSPDI_EXCEPTION_NO_DATE',
            `日曆 ${calendarIdForDiagnostics} 有一筆例外缺少 FromDate，已略過。`,
          );
        }
        continue;
      }
      exceptions.push({
        id: `exc-${calendarIdForDiagnostics}-${exceptions.length}`,
        name: childText(entry, 'Name') ?? 'Exception',
        from,
        to: to ?? from,
        working: dayWorking,
        workingTimes: dayWorking ? workingTimes : [],
      });
      continue;
    }

    const index = WEEKDAY_INDEX_BY_MSPDI_DAY_TYPE[dayType];
    if (index === undefined) {
      warn(ctx, 'MSPDI_UNKNOWN_DAYTYPE', `日曆 ${calendarIdForDiagnostics} 有未知的 DayType=${dayType}，已略過。`);
      continue;
    }
    const [firstWorkingTime, ...restWorkingTimes] = workingTimes;
    if (dayWorking && firstWorkingTime !== undefined) {
      const times: NonEmptyWorkingTimes = [firstWorkingTime, ...restWorkingTimes];
      mutableWeek[index] = { working: true, times };
    } else {
      if (dayWorking) {
        warn(
          ctx,
          'MSPDI_WORKING_DAY_NO_TIMES',
          `日曆 ${calendarIdForDiagnostics} 的 DayType=${dayType} 標記為工作日但沒有任何 WorkingTime，已視為非工作日。`,
        );
      }
      mutableWeek[index] = { working: false };
    }
  }

  const weekDays = mutableWeek as unknown as WeekDays;
  return { weekDays, exceptions };
}

function importWorkingTimes(dayNode: XmlNode): WorkingTime[] {
  const workingTimesNode = asNode(dayNode['WorkingTimes']);
  const entries = asArray(workingTimesNode?.['WorkingTime']).map(asNode).filter((n): n is XmlNode => n !== undefined);
  const result: WorkingTime[] = [];
  for (const entry of entries) {
    const from = parseTimeOfDay(childText(entry, 'FromTime'));
    const to = parseTimeOfDay(childText(entry, 'ToTime'));
    if (from === undefined || to === undefined || to <= from) {
      continue;
    }
    result.push({ start: minutes(from), end: minutes(to) });
  }
  return result;
}

/** 解析 `HH:mm:ss` 為距離當日 00:00 的分鐘數。 */
function parseTimeOfDay(value: string | undefined): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(value.trim());
  if (!match) {
    return undefined;
  }
  const [, hh, mm] = match;
  return Number(hh) * 60 + Number(mm);
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/** 取得 `<Tasks><Task>` 底下所有 Task 節點（多處共用，統一走這裡以確保順序一致）。 */
function getRawTaskNodes(root: XmlNode): XmlNode[] {
  const tasksNode = asNode(root['Tasks']);
  return asArray(tasksNode?.['Task']).map(asNode).filter((n): n is XmlNode => n !== undefined);
}

/**
 * 為所有 Task 節點配發 id（處理重複 UID，見 `resolveUniqueId`），寫入
 * `ctx.taskIdByNode`（每個節點自己的 id）與 `ctx.taskUidToId`（UID -> 第一筆的 id，
 * 供其他實體交叉參照）。必須在 importTasks / importDependencies / importAssignments /
 * importBaselines 之前執行一次，確保四處看到的 id 配發結果完全一致。
 */
function resolveAllTaskIds(root: XmlNode, ctx: ImportContext): void {
  for (const node of getRawTaskNodes(root)) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      continue;
    }
    const id = resolveUniqueId(ctx, ctx.usedTaskIds, ctx.taskUidToId, uid, (raw) => taskId(`t${raw}`), 'Task');
    ctx.taskIdByNode.set(node, id);
  }
}

function importTasks(root: XmlNode, ctx: ImportContext): Task[] {
  const rawTasks = getRawTaskNodes(root);

  const stack: Array<{ level: number; id: TaskId }> = [];
  const result: Task[] = [];

  for (const node of rawTasks) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      warn(ctx, 'MSPDI_TASK_NO_UID', `略過一筆缺少 UID 的 Task（Name="${childText(node, 'Name') ?? '(未命名)'}"）。`);
      continue;
    }
    const id = ctx.taskIdByNode.get(node) as TaskId;
    const outlineLevel = childNumber(node, 'OutlineLevel') ?? 1;

    while (stack.length > 0 && (stack[stack.length - 1] as { level: number }).level >= outlineLevel) {
      stack.pop();
    }
    const parentId = stack.length > 0 ? (stack[stack.length - 1] as { id: TaskId }).id : null;

    const task = buildTask(node, uid, id, outlineLevel, parentId, ctx);
    result.push(task);
    stack.push({ level: outlineLevel, id });
  }

  return result;
}

function buildTask(
  node: XmlNode,
  uid: number,
  id: TaskId,
  outlineLevel: number,
  parentId: TaskId | null,
  ctx: ImportContext,
): Task {
  const name = childText(node, 'Name') ?? `Task ${uid}`;
  const wbs = childText(node, 'WBS') ?? childText(node, 'OutlineNumber') ?? String(uid);

  const typeCode = childNumber(node, 'Type');
  const taskType: TaskType = (typeCode !== undefined ? TASK_TYPE_BY_MSPDI[typeCode] : undefined) ?? 'FixedUnits';

  const durationMinutes = parseDurationField(childText(node, 'Duration'), ctx, `Task ${uid} Duration`);
  const work = parseDurationField(childText(node, 'Work'), ctx, `Task ${uid} Work`) ?? minutes(0);

  const start = parseRequiredDateTime(ctx, childText(node, 'Start'), `Task ${uid} Start`, defaultStart());
  const finish = parseRequiredDateTime(ctx, childText(node, 'Finish'), `Task ${uid} Finish`, defaultFinish());
  const earlyStart = parseRequiredDateTime(ctx, childText(node, 'EarlyStart'), `Task ${uid} EarlyStart`, start);
  const earlyFinish = parseRequiredDateTime(ctx, childText(node, 'EarlyFinish'), `Task ${uid} EarlyFinish`, finish);
  const lateStart = parseRequiredDateTime(ctx, childText(node, 'LateStart'), `Task ${uid} LateStart`, start);
  const lateFinish = parseRequiredDateTime(ctx, childText(node, 'LateFinish'), `Task ${uid} LateFinish`, finish);

  // TotalSlack / FreeSlack：xsd:integer，單位為「十分之一分鐘」。
  const totalSlackRaw = childNumber(node, 'TotalSlack');
  const freeSlackRaw = childNumber(node, 'FreeSlack');

  const constraintCode = childNumber(node, 'ConstraintType');
  const constraintType: ConstraintType =
    (constraintCode !== undefined ? CONSTRAINT_TYPE_BY_MSPDI[constraintCode] : undefined) ?? 'ASAP';
  const constraintDate = parseOptionalDateTime(ctx, childText(node, 'ConstraintDate'), `Task ${uid} ConstraintDate`);
  const deadline = parseOptionalDateTime(ctx, childText(node, 'Deadline'), `Task ${uid} Deadline`);

  const percentComplete = childNumber(node, 'PercentComplete') ?? 0;
  const percentWorkComplete = childNumber(node, 'PercentWorkComplete') ?? 0;

  const calendarUid = childNumber(node, 'CalendarUID');
  const calendarId = calendarUid !== undefined ? ctx.calendarUidToId.get(calendarUid) ?? null : null;
  if (calendarUid !== undefined && calendarId === null) {
    warn(ctx, 'MSPDI_TASK_CALENDAR_NOT_FOUND', `Task ${uid} 參照到不存在的 CalendarUID=${calendarUid}。`);
  }

  const fixedCostAccrualCode = childNumber(node, 'FixedCostAccrual');
  const fixedCostAccrual: AccrualType =
    (fixedCostAccrualCode !== undefined ? ACCRUAL_TYPE_BY_MSPDI[fixedCostAccrualCode] : undefined) ?? 'Prorated';

  const priority = childNumber(node, 'Priority') ?? 500;

  return {
    id,
    uid,
    wbs,
    name,
    outlineLevel,
    parentId,
    durationMinutes: durationMinutes ?? minutes(0),
    work,
    start,
    finish,
    earlyStart,
    earlyFinish,
    lateStart,
    lateFinish,
    totalFloat: minutes(totalSlackRaw !== undefined ? totalSlackRaw / 10 : 0),
    freeFloat: minutes(freeSlackRaw !== undefined ? freeSlackRaw / 10 : 0),
    isCritical: childBoolean(node, 'Critical') ?? false,
    isSummary: childBoolean(node, 'Summary') ?? false,
    isMilestone: childBoolean(node, 'Milestone') ?? false,
    isManuallyScheduled: childBoolean(node, 'Manual') ?? false,
    taskType,
    effortDriven: childBoolean(node, 'EffortDriven') ?? false,
    constraintType,
    constraintDate,
    deadline,
    percentComplete: percent(percentComplete),
    percentWorkComplete: percent(percentWorkComplete),
    actualStart: parseOptionalDateTime(ctx, childText(node, 'ActualStart'), `Task ${uid} ActualStart`),
    actualFinish: parseOptionalDateTime(ctx, childText(node, 'ActualFinish'), `Task ${uid} ActualFinish`),
    priority,
    calendarId,
    ignoreResourceCalendar: childBoolean(node, 'IgnoreResourceCalendar') ?? false,
    fixedCost: money(parseCurrencyNumber(childText(node, 'FixedCost')) ?? 0),
    fixedCostAccrual,
    notes: childText(node, 'Notes') ?? '',
  };
}

function parseDurationField(
  value: string | undefined,
  ctx: ImportContext,
  fieldLabel: string,
): Minutes | undefined {
  if (value === undefined) {
    return undefined;
  }
  const parsed = parseIsoDuration(value, ctx.conversion);
  if (parsed === null) {
    warn(ctx, 'MSPDI_DURATION_UNPARSEABLE', `${fieldLabel} 無法解析的期間字串 "${value}"，以 0 取代。`);
    return minutes(0);
  }
  return parsed;
}

// ---------------------------------------------------------------------------
// Dependencies (PredecessorLink)
// ---------------------------------------------------------------------------

function importDependencies(root: XmlNode, ctx: ImportContext): Dependency[] {
  const rawTasks = getRawTaskNodes(root);

  const result: Dependency[] = [];
  for (const node of rawTasks) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      continue;
    }
    // 這個 Task 節點「自己」是誰：即使它的 UID 重複，也要用它自己的 id，
    // 不可誤用 `taskUidToId`（那只保留第一筆），否則重複 UID 那筆的
    // PredecessorLink 會被錯誤地掛到第一筆同 UID 的任務上。
    const successorId = ctx.taskIdByNode.get(node);
    if (successorId === undefined) {
      continue;
    }
    const links = asArray(node['PredecessorLink']).map(asNode).filter((n): n is XmlNode => n !== undefined);
    for (const link of links) {
      const predUid = childNumber(link, 'PredecessorUID');
      if (predUid === undefined) {
        warn(ctx, 'MSPDI_LINK_NO_UID', `Task ${uid} 有一筆 PredecessorLink 缺少 PredecessorUID，已略過。`);
        continue;
      }
      const predecessorId = ctx.taskUidToId.get(predUid);
      if (predecessorId === undefined) {
        warn(ctx, 'MSPDI_LINK_UNKNOWN_PREDECESSOR', `Task ${uid} 的 PredecessorUID=${predUid} 找不到對應任務。`);
        continue;
      }
      const typeCode = childNumber(link, 'Type');
      const type: DependencyType =
        (typeCode !== undefined ? DEPENDENCY_TYPE_BY_MSPDI[typeCode] : undefined) ?? 'FS';

      // LinkLag：xsd:integer，單位為「十分之一分鐘」（百分比格式下為十分之一百分比）。
      // LagFormat 與 Duration/Work 的 DurationFormat 共用 0-21 編碼：19/20 代表百分比 lag，
      // 4/6/8/10/12/20 代表 elapsed（跨越非工作時間，見 constants.ts 對照表與其 MPXJ 出處）。
      // elapsed 語意如果在匯入時被丟掉，同一個 lagMinutes 數值在排程時會被套用到錯誤的
      // 日曆規則（工作時間 vs. 連續日曆時間），造成看似數值沒變、實際落點日期卻不同的資料
      // 損毀，因此這裡務必把 lagIsElapsed 讀出來，並在 export.ts 對應寫回。
      const linkLagRaw = childNumber(link, 'LinkLag') ?? 0;
      const lagFormat = childNumber(link, 'LagFormat');
      const isPercent = lagFormat !== undefined && MSPDI_PERCENT_LAG_FORMATS.has(lagFormat);
      const isElapsed = lagFormat !== undefined && MSPDI_ELAPSED_LAG_FORMATS.has(lagFormat);

      const dependency: Dependency = {
        id: dependencyId(`d-${predUid}-${uid}-${result.length}`),
        predecessorId,
        successorId,
        type,
        lagMinutes: isPercent ? minutes(linkLagRaw * 10) : minutes(linkLagRaw / 10),
        ...(isPercent ? { lagIsPercent: true } : {}),
        ...(isElapsed ? { lagIsElapsed: true } : {}),
      };
      result.push(dependency);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

function importResources(root: XmlNode, ctx: ImportContext): Resource[] {
  const resourcesNode = asNode(root['Resources']);
  const rawResources = asArray(resourcesNode?.['Resource']).map(asNode).filter((n): n is XmlNode => n !== undefined);

  const result: Resource[] = [];
  for (const node of rawResources) {
    const uid = childNumber(node, 'UID');
    if (uid === undefined) {
      warn(ctx, 'MSPDI_RESOURCE_NO_UID', `略過一筆缺少 UID 的 Resource（Name="${childText(node, 'Name') ?? '(未命名)'}"）。`);
      continue;
    }
    const id = resolveUniqueId(ctx, ctx.usedResourceIds, ctx.resourceUidToId, uid, (raw) => resourceId(`r${raw}`), 'Resource');

    const typeCode = childNumber(node, 'Type');
    const type: ResourceType = (typeCode !== undefined ? RESOURCE_TYPE_BY_MSPDI[typeCode] : undefined) ?? 'Work';

    const standardRateFormat = childNumber(node, 'StandardRateFormat');
    const overtimeRateFormat = childNumber(node, 'OvertimeRateFormat');
    const standardRate = parseRatePerMinute(childText(node, 'StandardRate'), standardRateFormat);
    const overtimeRate = parseRatePerMinute(childText(node, 'OvertimeRate'), overtimeRateFormat);
    const rateTables = importRateTables(node, ctx, uid);

    const calendarUid = childNumber(node, 'CalendarUID');
    const calendarId = calendarUid !== undefined ? ctx.calendarUidToId.get(calendarUid) ?? null : null;

    result.push({
      id,
      uid,
      name: childText(node, 'Name') ?? `Resource ${uid}`,
      type,
      ...(childBoolean(node, 'IsBudget') === true ? { isBudget: true } : {}),
      initials: childText(node, 'Initials') ?? '',
      group: childText(node, 'Group') ?? '',
      maxUnits: units(childNumber(node, 'MaxUnits') ?? 1),
      standardRate,
      overtimeRate,
      costPerUse: money(parseCurrencyNumber(childText(node, 'CostPerUse')) ?? 0),
      ...(rateTables ? { rateTables } : {}),
      accrueAt: (() => {
        const code = childNumber(node, 'AccrueAt');
        return (code !== undefined ? ACCRUAL_TYPE_BY_MSPDI[code] : undefined) ?? 'Prorated';
      })(),
      calendarId,
      materialLabel: type === 'Material' ? childText(node, 'MaterialLabel') ?? null : null,
      availability: importAvailability(node, ctx, uid),
      notes: childText(node, 'Notes'),
    });
  }
  return result;
}

function importRateTables(node: XmlNode, ctx: ImportContext, resourceUid: number): ResourceRateTables | undefined {
  const entries = asArray(asNode(node['Rates'])?.['Rate']).map(asNode).filter((value): value is XmlNode => value !== undefined);
  if (entries.length === 0) return undefined;
  const tables: Partial<Record<RateTableName, import('../../domain/resource.js').ResourceRatePeriod[]>> = {};
  for (const entry of entries) {
    const code = childNumber(entry, 'RateTable') ?? 0;
    if (!Number.isInteger(code) || code < 0 || code > 4) { warn(ctx, 'MSPDI_RATE_TABLE_INVALID', `Resource ${resourceUid} has unknown RateTable=${code}; skipped.`); continue; }
    const effectiveFrom = parseOptionalDateTime(ctx, childText(entry, 'RatesFrom'), `Resource ${resourceUid} RatesFrom`);
    if (!effectiveFrom) { warn(ctx, 'MSPDI_RATE_TABLE_NO_DATE', `Resource ${resourceUid} rate row has no RatesFrom; skipped.`); continue; }
    const table = ['A', 'B', 'C', 'D', 'E'][code] as RateTableName;
    const rows = tables[table] ?? [];
    rows.push({ effectiveFrom, standardRate: parseRatePerMinute(childText(entry, 'StandardRate'), childNumber(entry, 'StandardRateFormat')), overtimeRate: parseRatePerMinute(childText(entry, 'OvertimeRate'), childNumber(entry, 'OvertimeRateFormat')), costPerUse: money(parseCurrencyNumber(childText(entry, 'CostPerUse')) ?? 0) });
    tables[table] = rows;
  }
  for (const table of ['A', 'B', 'C', 'D', 'E'] as const) {
    const rows = tables[table];
    if (!rows) continue;
    rows.sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : 0));
    const valid = rows.filter((row, index) => {
      const previous = index === 0 ? undefined : rows[index - 1];
      const numbers = [row.standardRate as number, row.overtimeRate as number, row.costPerUse as number];
      const ok = numbers.every((value) => Number.isFinite(value) && value >= 0) && previous?.effectiveFrom !== row.effectiveFrom;
      if (!ok) warn(ctx, 'MSPDI_RATE_TABLE_INVALID', `Resource ${resourceUid} ${table} rate row is duplicate or invalid; skipped.`);
      return ok;
    });
    if (valid.length === 0) delete tables[table]; else tables[table] = valid;
  }
  return Object.keys(tables).length === 0 ? undefined : tables;
}

function parseRatePerMinute(value: string | undefined, formatCode: number | undefined) {
  const numeric = parseCurrencyNumber(value) ?? 0;
  const divisor = rateDivisorFromFormatCode(formatCode, value);
  return ratePerMinute(numeric / divisor);
}

function rateDivisorFromFormatCode(formatCode: number | undefined, rawValue: string | undefined): number {
  // 標準 MSPDI 用獨立的 *RateFormat 欄位表示單位；但有些匯出工具會把單位
  // 直接串在字串內（例如 "50/h"），此處雙管齊下以求穩健。
  if (rawValue !== undefined) {
    if (/\/m(in)?\b/i.test(rawValue)) return 1;
    if (/\/d\b/i.test(rawValue)) return 60 * 24;
    if (/\/w\b/i.test(rawValue)) return 60 * 24 * 7;
    if (/\/mo\b/i.test(rawValue)) return 60 * 24 * 30;
    if (/\/y\b/i.test(rawValue)) return 60 * 24 * 365;
    if (/\/h\b/i.test(rawValue)) return 60;
  }
  switch (formatCode) {
    case 1:
      return 1;
    case 2:
      return 60;
    case 3:
      return 60 * 24;
    case 4:
      return 60 * 24 * 7;
    case 5:
      return 60 * 24 * 30;
    case 7:
      return 60 * 24 * 365;
    default:
      return 60;
  }
}

function importAvailability(resourceNode: XmlNode, ctx: ImportContext, resourceUid: number): ResourceAvailability[] {
  const availNode = asNode(resourceNode['AvailabilityPeriods']);
  const entries = asArray(availNode?.['AvailabilityPeriod']).map(asNode).filter((n): n is XmlNode => n !== undefined);
  const result: ResourceAvailability[] = [];
  for (const entry of entries) {
    const from = parseOptionalDate(ctx, childText(entry, 'AvailableFrom'), `Resource ${resourceUid} AvailableFrom`);
    if (!from) {
      continue;
    }
    const to = parseOptionalDate(ctx, childText(entry, 'AvailableTo'), `Resource ${resourceUid} AvailableTo`);
    const maxUnits = childNumber(entry, 'AvailableUnits') ?? 1;
    result.push({ from, to, maxUnits: units(maxUnits) });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

function importAssignments(root: XmlNode, ctx: ImportContext, tasks: readonly Task[]): Assignment[] {
  const assignmentsNode = asNode(root['Assignments']);
  const rawAssignments = asArray(assignmentsNode?.['Assignment']).map(asNode).filter((n): n is XmlNode => n !== undefined);

  const tasksByUid = new Map<number, Task>();
  for (const t of tasks) {
    tasksByUid.set(t.uid, t);
  }

  // Pre-scan：找出所有「明確 UID」的最大值，讓缺 UID 的指派合成出的 uid 一定大於
  // 檔案中任何一筆明確 UID —— 不論該明確 UID 出現在合成點之前還是之後。原本用
  // `result.length + 1` 只考慮「目前已處理筆數」，若後面才出現同值的明確 UID，
  // 或前面已略過幾筆，合成值就可能撞上明確 UID，導致 baseline／交叉參照對照表被覆寫。
  let maxExplicitAssignmentUid = 0;
  for (const node of rawAssignments) {
    const explicitUid = childNumber(node, 'UID');
    if (explicitUid !== undefined && explicitUid > maxExplicitAssignmentUid) {
      maxExplicitAssignmentUid = explicitUid;
    }
  }
  let nextSyntheticAssignmentUid = maxExplicitAssignmentUid + 1;

  const result: Assignment[] = [];
  for (const node of rawAssignments) {
    const uid = childNumber(node, 'UID');
    const taskUid = childNumber(node, 'TaskUID');
    const resourceUid = childNumber(node, 'ResourceUID');
    // MS Project stores budget allocations as project-summary (TaskUID=0)
    // assignments. They are consumed by importBudgetAssignments(), never as
    // ordinary assignments (and must not emit an unknown-task diagnostic).
    if (taskUid === 0) continue;
    if (taskUid === undefined || resourceUid === undefined) {
      warn(
        ctx,
        'MSPDI_ASSIGNMENT_MISSING_REF',
        `略過一筆缺少 TaskUID/ResourceUID 的 Assignment（UID=${uid ?? '(無 UID)'}）。`,
      );
      continue;
    }
    const taskRef = ctx.taskUidToId.get(taskUid);
    const resourceRef = ctx.resourceUidToId.get(resourceUid);
    if (taskRef === undefined || resourceRef === undefined) {
      warn(
        ctx,
        'MSPDI_ASSIGNMENT_UNKNOWN_REF',
        `Assignment（UID=${uid ?? '(無 UID)'}）參照到不存在的 Task/Resource (task=${taskUid}, resource=${resourceUid})。`,
      );
      continue;
    }

    let id: AssignmentId;
    let assignUid: number;
    if (uid !== undefined) {
      // 明確 UID：走與 Calendar/Task/Resource 相同的重複 UID 處理規則。
      assignUid = uid;
      id = resolveUniqueId(ctx, ctx.usedAssignmentIds, ctx.assignmentUidToId, uid, (raw) => assignmentId(`a${raw}`), 'Assignment');
    } else {
      // 缺 UID：從「已知最大明確 UID + 1」起跳配發，並用遞增計數器確保彼此也不衝突；
      // 不寫入 assignmentUidToId —— 合成 uid 不對應任何 XML 中的真實 UID，不該被
      // 其他實體透過 UID 交叉參照解析到。
      assignUid = nextSyntheticAssignmentUid;
      nextSyntheticAssignmentUid += 1;
      id = assignmentId(`a${assignUid}`);
      ctx.usedAssignmentIds.add(id);
    }
    // 這個節點自己的 id：importBaselines 要把 <Assignment><Baseline> 掛回這一筆
    // 指派本身時，必須查這裡，不能查 `assignmentUidToId`（那只保留第一筆）。
    ctx.assignmentIdByNode.set(node, id);

    const fallbackTask = tasksByUid.get(taskUid);
    const start = parseRequiredDateTime(
      ctx,
      childText(node, 'Start'),
      `Assignment ${assignUid} Start`,
      fallbackTask?.start ?? defaultStart(),
    );
    const finish = parseRequiredDateTime(
      ctx,
      childText(node, 'Finish'),
      `Assignment ${assignUid} Finish`,
      fallbackTask?.finish ?? defaultFinish(),
    );

    const work = parseDurationField(childText(node, 'Work'), ctx, `Assignment ${assignUid} Work`) ?? minutes(0);
    const actualWork =
      parseDurationField(childText(node, 'ActualWork'), ctx, `Assignment ${assignUid} ActualWork`) ?? minutes(0);
    const remainingWork =
      parseDurationField(childText(node, 'RemainingWork'), ctx, `Assignment ${assignUid} RemainingWork`) ??
      minutes(Math.max(0, (work as unknown as number) - (actualWork as unknown as number)));

    result.push({
      id,
      taskId: taskRef,
      resourceId: resourceRef,
      ...(rateTableName(childNumber(node, 'RateTable')) ? { rateTable: rateTableName(childNumber(node, 'RateTable')) } : {}),
      units: units(childNumber(node, 'Units') ?? 1),
      work,
      actualWork,
      remainingWork,
      start,
      finish,
      cost: money(parseCurrencyNumber(childText(node, 'Cost')) ?? 0),
      actualCost: money(parseCurrencyNumber(childText(node, 'ActualCost')) ?? 0),
      timephased: importTimephased(node, ctx, assignUid),
      notes: childText(node, 'Notes'),
    });
  }
  return result;
}

function rateTableName(code: number | undefined): RateTableName | undefined {
  return code !== undefined && Number.isInteger(code) && code >= 0 && code <= 4 ? (['A', 'B', 'C', 'D', 'E'][code] as RateTableName) : undefined;
}

function importBudgetAssignments(root: XmlNode, ctx: ImportContext, resources: readonly Resource[]): BudgetAssignment[] {
  const byUid = new Map(resources.map((resource) => [resource.uid, resource]));
  const nodes = asArray(asNode(root['Assignments'])?.['Assignment']).map(asNode).filter((node): node is XmlNode => node !== undefined);
  const result: BudgetAssignment[] = [];
  const seenResources = new Set<string>();
  for (const node of nodes) {
    if (childNumber(node, 'TaskUID') !== 0) continue;
    const resourceUid = childNumber(node, 'ResourceUID');
    const resource = resourceUid === undefined ? undefined : byUid.get(resourceUid);
    if (!resource?.isBudget) continue;
    if (seenResources.has(resource.id as string)) { warn(ctx, 'MSPDI_BUDGET_ASSIGNMENT_DUPLICATE', `Budget resource ${resourceUid} has duplicate allocation; kept first.`); continue; }
    const work = parseDurationField(childText(node, 'BudgetWork'), ctx, `Budget assignment ${resourceUid} Work`);
    const costValue = parseCurrencyNumber(childText(node, 'BudgetCost'));
    if ((work === undefined) === (costValue === undefined) || (resource.type === 'Cost' ? work !== undefined : costValue !== undefined)) { warn(ctx, 'MSPDI_BUDGET_ASSIGNMENT_INVALID', `Budget assignment ${resourceUid} does not match budget resource type; skipped.`); continue; }
    seenResources.add(resource.id as string);
    result.push({ id: `budget-${resource.uid}`, resourceId: resource.id, ...(work !== undefined ? { budgetWork: work } : {}), ...(costValue !== undefined ? { budgetCost: money(costValue) } : {}) });
  }
  return result;
}

const WORK_TIMEPHASED_TYPES = new Set([0, 1, 2, 3, 4]);
const COST_TIMEPHASED_TYPES = new Set([5, 6]);

function importTimephased(assignmentNode: XmlNode, ctx: ImportContext, assignUid: number): TimephasedSet | null {
  const entries = asArray(assignmentNode['TimephasedData']).map(asNode).filter((n): n is XmlNode => n !== undefined);
  if (entries.length === 0) {
    return null;
  }

  const work: TimephasedWorkSlice[] = [];
  const actualWork: TimephasedWorkSlice[] = [];
  const remainingWork: TimephasedWorkSlice[] = [];
  const overtimeWork: TimephasedWorkSlice[] = [];
  const baselineWork: TimephasedWorkSlice[] = [];
  const cost: TimephasedCostSlice[] = [];
  const actualCost: TimephasedCostSlice[] = [];
  const baselineCost: TimephasedCostSlice[] = [];

  for (const entry of entries) {
    const typeCode = childNumber(entry, 'Type');
    const start = parseOptionalDateTime(ctx, childText(entry, 'Start'), `Assignment ${assignUid} TimephasedData Start`);
    const finish = parseOptionalDateTime(ctx, childText(entry, 'Finish'), `Assignment ${assignUid} TimephasedData Finish`);
    if (typeCode === undefined || !start || !finish) {
      continue;
    }
    const rawValue = childText(entry, 'Value');

    if (WORK_TIMEPHASED_TYPES.has(typeCode)) {
      const valueMinutes = rawValue !== undefined ? parseIsoDuration(rawValue, ctx.conversion) : null;
      if (valueMinutes === null) {
        continue;
      }
      const slice: TimephasedWorkSlice = { kind: 'Work', start, end: finish, valueMinutes };
      switch (typeCode) {
        case 0:
          work.push(slice);
          break;
        case 1:
          remainingWork.push(slice);
          break;
        case 2:
          actualWork.push(slice);
          break;
        case 3:
          overtimeWork.push(slice);
          break;
        case 4:
          baselineWork.push(slice);
          break;
        default:
          break;
      }
    } else if (COST_TIMEPHASED_TYPES.has(typeCode)) {
      const valueCost = money(parseCurrencyNumber(rawValue) ?? 0);
      const slice: TimephasedCostSlice = { kind: 'Cost', start, end: finish, valueCost };
      if (typeCode === 5) {
        baselineCost.push(slice);
      } else {
        actualCost.push(slice);
      }
    } else {
      info(ctx, 'MSPDI_TIMEPHASED_TYPE_UNSUPPORTED', `Assignment ${assignUid} 的 TimephasedData Type=${typeCode} 不支援，已略過。`);
    }
  }

  return {
    granularity: 'Days',
    work,
    actualWork,
    remainingWork,
    ...(overtimeWork.length > 0 ? { overtimeWork } : {}),
    cost,
    actualCost,
    ...(baselineWork.length > 0 ? { baselineWork } : {}),
    ...(baselineCost.length > 0 ? { baselineCost } : {}),
  };
}

// ---------------------------------------------------------------------------
// Baselines
// ---------------------------------------------------------------------------

function importBaselines(root: XmlNode, ctx: ImportContext, budgetAssignments: readonly BudgetAssignment[]): Baseline[] {
  const byNumber = new Map<number, { tasks: BaselineTaskSnapshot[]; assignments: BaselineAssignmentSnapshot[]; budgetAssignments: import('../../domain/baseline.js').BaselineBudgetSnapshot[] }>();

  const ensure = (n: number) => {
    let bucket = byNumber.get(n);
    if (!bucket) {
      bucket = { tasks: [], assignments: [], budgetAssignments: [] };
      byNumber.set(n, bucket);
    }
    return bucket;
  };

  const rawTasks = getRawTaskNodes(root);
  for (const node of rawTasks) {
    const uid = childNumber(node, 'UID');
    // 用節點自己的 id（處理重複 UID），不是 `taskUidToId`（只保留第一筆）。
    const id = ctx.taskIdByNode.get(node);
    if (id === undefined) {
      continue;
    }
    const baselineNodes = asArray(node['Baseline']).map(asNode).filter((n): n is XmlNode => n !== undefined);
    for (const baselineNode of baselineNodes) {
      const number = attrNumber(baselineNode, 'Number') ?? childNumber(baselineNode, 'Number');
      if (number === undefined) {
        continue;
      }
      const start = parseOptionalDateTime(ctx, childText(baselineNode, 'Start'), `Task ${uid} Baseline${number} Start`);
      const finish = parseOptionalDateTime(ctx, childText(baselineNode, 'Finish'), `Task ${uid} Baseline${number} Finish`);
      if (!start || !finish) {
        warn(ctx, 'MSPDI_BASELINE_MISSING_DATES', `Task ${uid} Baseline${number} 缺少可用的 Start/Finish，已略過此筆基準線快照。`);
        continue;
      }
      const duration = parseDurationField(childText(baselineNode, 'Duration'), ctx, `Task ${uid} Baseline${number}`);
      const work = parseDurationField(childText(baselineNode, 'Work'), ctx, `Task ${uid} Baseline${number} Work`);
      ensure(number).tasks.push({
        taskId: id,
        start,
        finish,
        durationMinutes: duration ?? minutes(0),
        work: work ?? minutes(0),
        cost: money(parseCurrencyNumber(childText(baselineNode, 'Cost')) ?? 0),
        fixedCost: money(parseCurrencyNumber(childText(baselineNode, 'FixedCost')) ?? 0),
      });
    }
  }

  const assignmentsNode = asNode(root['Assignments']);
  const rawAssignments = asArray(assignmentsNode?.['Assignment']).map(asNode).filter((n): n is XmlNode => n !== undefined);
  for (const node of rawAssignments) {
    const uid = childNumber(node, 'UID');
    const taskUid = childNumber(node, 'TaskUID');
    if (uid === undefined || taskUid === undefined) {
      continue;
    }
    if (taskUid === 0) {
      const resourceUid = childNumber(node, 'ResourceUID');
      const budget = budgetAssignments.find((item) => resourceUid !== undefined && (item.resourceId as string) === (ctx.resourceUidToId.get(resourceUid) as string));
      if (!budget) continue;
      for (const baselineNode of asArray(node['Baseline']).map(asNode).filter((value): value is XmlNode => value !== undefined)) {
        const number = attrNumber(baselineNode, 'Number') ?? childNumber(baselineNode, 'Number');
        if (number === undefined) continue;
        const work = parseDurationField(childText(baselineNode, 'BudgetWork'), ctx, `Budget assignment ${uid} Baseline${number} Work`);
        const cost = parseCurrencyNumber(childText(baselineNode, 'BudgetCost'));
        if ((work === undefined) === (cost === undefined)) continue;
        ensure(number).budgetAssignments.push({ budgetAssignmentId: budget.id, resourceId: budget.resourceId, ...(work !== undefined ? { budgetWork: work } : {}), ...(cost !== undefined ? { budgetCost: money(cost) } : {}) });
      }
      continue;
    }
    // 用這個節點自己的 id（處理重複 UID），不是 `assignmentUidToId`（只保留第一筆）——
    // 否則重複 UID 的第二筆指派，它自己的 <Baseline> 會被誤掛到第一筆同 UID 的指派上。
    const assignmentIdValue = ctx.assignmentIdByNode.get(node);
    // TaskUID 是「參照到哪個任務」，語意上是模稜兩可時取第一筆，故仍用 taskUidToId。
    const taskIdValue = ctx.taskUidToId.get(taskUid);
    if (assignmentIdValue === undefined || taskIdValue === undefined) {
      continue;
    }
    const baselineNodes = asArray(node['Baseline']).map(asNode).filter((n): n is XmlNode => n !== undefined);
    for (const baselineNode of baselineNodes) {
      const number = attrNumber(baselineNode, 'Number') ?? childNumber(baselineNode, 'Number');
      if (number === undefined) {
        continue;
      }
      const start = parseOptionalDateTime(ctx, childText(baselineNode, 'Start'), `Assignment ${uid} Baseline${number} Start`);
      const finish = parseOptionalDateTime(ctx, childText(baselineNode, 'Finish'), `Assignment ${uid} Baseline${number} Finish`);
      if (!start || !finish) {
        warn(ctx, 'MSPDI_BASELINE_MISSING_DATES', `Assignment ${uid} Baseline${number} 缺少可用的 Start/Finish，已略過此筆基準線快照。`);
        continue;
      }
      const work = parseDurationField(childText(baselineNode, 'Work'), ctx, `Assignment ${uid} Baseline${number} Work`);
      ensure(number).assignments.push({
        assignmentId: assignmentIdValue,
        taskId: taskIdValue,
        start,
        finish,
        work: work ?? minutes(0),
        cost: money(parseCurrencyNumber(childText(baselineNode, 'Cost')) ?? 0),
      });
    }
  }

  // 此處不重複記診斷：StatusDate/StartDate 已在 importMspdi() 主流程解析並記過診斷，
  // 這裡僅是取值做 fallback，用 parseMspdiDateTime 直接讀（絕不 throw，解析失敗回傳 null）。
  const statusDate = parseMspdiDateTime(childText(root, 'StatusDate'));
  const startDate = parseMspdiDateTime(childText(root, 'StartDate'));
  const savedAt = statusDate ?? startDate ?? defaultStart();

  const result: Baseline[] = [];
  for (const [number, bucket] of byNumber) {
    if (number < 0 || number > 10) {
      warn(ctx, 'MSPDI_BASELINE_OUT_OF_RANGE', `Baseline Number=${number} 超出 0-10 範圍，已略過。`);
      continue;
    }
    result.push({
      number: number as BaselineNumber,
      name: number === 0 ? 'Baseline' : `Baseline${number}`,
      savedAt,
      tasks: bucket.tasks,
      assignments: bucket.assignments,
      ...(bucket.budgetAssignments.length > 0 ? { budgetAssignments: bucket.budgetAssignments } : {}),
    });
  }
  result.sort((a, b) => a.number - b.number);
  return result;
}
